/**
 * Shared multi-tenant auth helpers for role-based shells.
 * Session requires JWT (token) — legacy uid-only sessions are not valid.
 */
export function getSessionAuth() {
    return {
        token: sessionStorage.getItem('token'),
        refreshToken: sessionStorage.getItem('refreshToken'),
        uid: sessionStorage.getItem('uid'),
        email: sessionStorage.getItem('email'),
        mName: sessionStorage.getItem('mName'),
        role: sessionStorage.getItem('role'),
        orgRole: sessionStorage.getItem('orgRole'),
        organizationId: sessionStorage.getItem('organizationId'),
        orgSlug: sessionStorage.getItem('orgSlug'),
        platformAdmin: sessionStorage.getItem('platformAdmin') === 'true',
        auth: sessionStorage.getItem('auth') === 'true' && Boolean(sessionStorage.getItem('token')),
    };
}

export function clearSessionAuth() {
    [
        'token', 'refreshToken', 'uid', 'email', 'mName', 'auth', 'type', 'role',
        'orgRole', 'organizationId', 'orgSlug', 'platformAdmin', 'uploadLimits',
    ].forEach((k) => sessionStorage.removeItem(k));
    try {
        localStorage.removeItem('user');
    } catch {
        /* ignore */
    }
}

/**
 * Post-login home path by platform/org role.
 */
export function resolveHomePath({ platformAdmin, orgRole, role } = getSessionAuth()) {
    if (platformAdmin) return '/super-admin';
    if (orgRole === 'org_admin') return '/org/admin';
    if (orgRole === 'instructor' || role === 'instructor' || role === 'teacher' || role === 'admin') {
        return '/studio';
    }
    return '/learn';
}

export function authHeaders(extra = {}) {
    const { token, organizationId } = getSessionAuth();
    const headers = { ...extra };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (organizationId) headers['X-Organization-Id'] = organizationId;
    return headers;
}

/** Persist org/role fields from /api/auth/me (or login payload). */
export function applySessionClaims(claims = {}) {
    let changed = false;
    if (claims.organizationId) {
        const next = String(claims.organizationId);
        if (sessionStorage.getItem('organizationId') !== next) changed = true;
        sessionStorage.setItem('organizationId', next);
    }
    if (claims.orgRole) {
        sessionStorage.setItem('orgRole', String(claims.orgRole));
        changed = true;
    }
    if (claims.orgSlug) {
        sessionStorage.setItem('orgSlug', String(claims.orgSlug));
        changed = true;
    }
    if (claims.platformAdmin != null) {
        sessionStorage.setItem('platformAdmin', claims.platformAdmin ? 'true' : 'false');
        changed = true;
    }
    if (claims.role) {
        sessionStorage.setItem('role', String(claims.role));
    }
    if (claims.email) {
        sessionStorage.setItem('email', String(claims.email));
    }
    if (claims.mName) {
        sessionStorage.setItem('mName', String(claims.mName));
    }
    if (claims.uid || claims._id) {
        sessionStorage.setItem('uid', String(claims.uid || claims._id));
    }
    if (changed && typeof window !== 'undefined') {
        try {
            window.dispatchEvent(new CustomEvent('coursolo:auth-claims'));
        } catch { /* ignore */ }
    }
}

/**
 * Persist a full login/signup response into sessionStorage.
 * Requires accessToken — rejects incomplete legacy payloads.
 */
export function persistLoginSession(userData = {}, uploadLimits, authExtras = {}) {
    const token = authExtras.accessToken || authExtras.token;
    if (!token) {
        throw new Error('Login response missing JWT — legacy sessions are not supported');
    }
    const uid = userData._id || userData.userId || authExtras.uid;
    sessionStorage.setItem('token', token);
    if (authExtras.refreshToken) {
        sessionStorage.setItem('refreshToken', authExtras.refreshToken);
    }
    sessionStorage.setItem('auth', 'true');
    if (userData.email) sessionStorage.setItem('email', String(userData.email));
    if (userData.mName) sessionStorage.setItem('mName', String(userData.mName));
    if (uid) sessionStorage.setItem('uid', String(uid));
    if (userData.type) sessionStorage.setItem('type', String(userData.type));
    sessionStorage.setItem('role', String(userData.role || authExtras.role || 'student'));
    applySessionClaims({
        organizationId: authExtras.organizationId,
        orgRole: authExtras.orgRole,
        orgSlug: authExtras.orgSlug,
        platformAdmin: authExtras.platformAdmin,
    });
    if (uploadLimits) {
        sessionStorage.setItem('uploadLimits', JSON.stringify(uploadLimits));
    }
    try {
        localStorage.setItem('user', JSON.stringify(userData));
    } catch {
        /* storage full — ignore */
    }
}

export function currentBrowserHost() {
    if (typeof window === 'undefined') return '';
    return window.location.host || '';
}
